import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  BADGE_ATTRIBUTE_AMBASSADOR,
  BADGE_ATTRIBUTE_ATTENDEE,
  BADGE_ATTRIBUTE_HOST,
} from "utils/helpers/enums/shared.enum";
import { ROLES } from "utils/helpers/enums/roles.enum";
import { DeleteSvgRed, ArrowBackIcon } from "assets/images/common/svgs";
import FormErrorMessage from "components/forms/form-error-message";
import useAuth from "containers/auth/useHook";
import useBadges from "../../useHooks";
import { warningToaster } from "utils/helpers/common/alert-service";
import { useLocation, useNavigate } from "react-router-dom";

const CreateAchievementBadges = () => {
  const [formData, setFormData] = useState({
    name: "",
    imageUrl: "",
    imageId: null,
    attributes: [] as { attribute: string; value: string }[],
  });

  const { uploadFile } = useAuth();
  const { createBadge } = useBadges();
  const navigate = useNavigate();
  const location = useLocation();
  const selectedRole = location.state?.role;

  const {
    register,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm();

  // Attribute list depends on selected role
  const getAttributeOptions = () => {
    if (selectedRole === ROLES.HOST) return Object.values(BADGE_ATTRIBUTE_HOST);
    if (selectedRole === ROLES.AMBASSADOR)
      return Object.values(BADGE_ATTRIBUTE_AMBASSADOR);
    return Object.values(BADGE_ATTRIBUTE_ATTENDEE);
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setFormData((prev) => ({ ...prev, imageUrl: file.name }));

    const picture = await uploadFile(file);
    if (picture) {
      setFormData((prev) => ({ ...prev, imageId: picture.id }));
    }
  };

  // Handle adding attribute on select
  const handleAttributeSelect = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedAttr = event.target.value;
    if (!selectedAttr) return;

    // Check if attribute already added
    const alreadyAdded = formData.attributes.some(
      (attr) => attr.attribute === selectedAttr
    );
    if (!alreadyAdded) {
      setFormData((prev) => ({
        ...prev,
        attributes: [...prev.attributes, { attribute: selectedAttr, value: "" }],
      }));
    }
    event.target.value = ""; // reset select input for next selection
  };

  // Handle attribute value change
  const handleAttributeValueChange = (
    index: number,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    // Prevent negative values
    if (parseFloat(value) < 0) return;
    
    const newAttributes = [...formData.attributes];
    newAttributes[index].value = value;
    setFormData((prev) => ({ ...prev, attributes: newAttributes }));
  };

  // Remove attribute row
  const removeAttribute = (index: number) => {
    const newAttributes = [...formData.attributes];
    newAttributes.splice(index, 1);
    setFormData((prev) => ({ ...prev, attributes: newAttributes }));
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = event.target;
    setValue(name, value, { shouldValidate: true });
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    if (!selectedRole) {
      warningToaster("Role not selected");
      setTimeout(() => {
        navigate(-1);
      }, 1500);
    }
  }, [selectedRole]);

  const onSubmit = async () => {
    if (!formData.imageId) {
      warningToaster("Badge image is required");
      return;
    }
    const attributesObj: Record<string, any> = {};
    formData.attributes.forEach(({ attribute, value }) => {
      attributesObj[attribute] = value;
    });

    const payload: any = {
      name: formData.name,
      badgeType: "achievement",
      criteria: attributesObj,
      imageId: formData.imageId,
      forRole: selectedRole ? selectedRole.toUpperCase() : "",
    };

    await createBadge(payload);


  };


  const goToBack = () => {
    navigate(-1);
  };

  return (
    <div>
      <h1 className="text-base text-primaryText font-bold flex items-center gap-3">
        <span
          onClick={goToBack}
          className="bg-transparentBlueBg w-[44px] h-[44px] rounded-full flex items-center justify-center cursor-pointer"
        >
          <ArrowBackIcon />
        </span>
        Create a new Badge
      </h1>
      <form onSubmit={handleSubmit(onSubmit)} className="mt-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {/* Badge Title */}
          <div className="flex flex-col gap-1">
            <label className="custom-label">
              Badge Title
            </label>
            <input
              type="text"
              className="custom-input"
              {...register("name", { required: true })}
              value={formData.name}
              onChange={handleChange}
            />
            <FormErrorMessage error={errors.name} />
          </div>

          {/* Upload Badge */}
          <div className="flex flex-col gap-1">
            <label className="custom-label">
              Upload Badge
            </label>
            <div className="relative flex items-center">
              <label className="bg-gray-200 h-[51px] px-4 py-2 rounded-lg cursor-pointer text-center flex items-center justify-center">
                + Upload
                <input
                  type="file"
                  className="hidden"
                  onChange={handleFileChange}
                />
              </label>
              <span className="ml-2 text-gray-500 text-base">
                {formData.imageUrl ?? "Badge Size: 48 x 48 px"}
              </span>
            </div>
          </div>

          {/* Select multiple attributes */}
          {selectedRole && (
            <div className="flex flex-col gap-1">
              <label className="custom-label">
                Add Attribute
              </label>
              <select
                className="custom-input"
                onChange={handleAttributeSelect}
                defaultValue=""
              >
                <option value="" disabled>
                  Select
                </option>
                {getAttributeOptions().map((attr, index) => (
                  <option key={index} value={attr} className="capitalize">
                    {attr.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim()}
                  </option>
                ))}
              </select>
              <FormErrorMessage error={errors.attribute} />
            </div>
          )}
        </div>

        {/* Render all selected attributes with input and delete */}
        {formData.attributes.length > 0 && (
          <div className="flex flex-col gap-1">
            <h1 className="text-xl text-primaryText font-bold mt-6 mb-4">
              Set Attributes
            </h1>
            <div className="flex flex-wrap gap-4">
              {formData.attributes.map(({ attribute, value }, index) => (
                <div
                  key={attribute}
                  className="flex items-center gap-4"
                  style={{ minWidth: 300 }}
                >
                  <div className="flex-1">
                    <label className="block custom-label mb-1">
                      {attribute.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim()}
                    </label>
                    <input
                      type="number"
                      min="0"
                      className="custom-input"
                      value={value}
                      onChange={(e) => handleAttributeValueChange(index, e)}
                      required
                    />
                  </div>
                  <button
                    type="button"
                    className="cursor-pointer mt-7 h-[50px]"
                    onClick={() => removeAttribute(index)}
                    aria-label={`Delete ${attribute}`}
                  >
                    <DeleteSvgRed />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Save Button */}
        <div className="mt-6">
          <button
            className="bg-darkSlateBlueBg text-white px-6 py-2 rounded-lg"
            type="submit"
          >
            Save
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateAchievementBadges;
